import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ILast7DaysHourTransactionDashboard, ITransaction } from '../interface/transaction';
import { IMatter } from '../interface/matter';
import { IPrincipalCompany } from '../interface/principalCompany';
import { IPrincipalPrivatePerson } from '../interface/principalPrivatePerson';
import {
  applyRoundingRuleToBillableHours,
  areStringsEqual,
  usesCommaDecimalSeparator,
} from './utilityFunctions';
import { InvoiceableDurationStep, MAX_FILE_SIZE, SystemConstants } from './constants';
import { graphApiBaseUrl } from '../env-variables.config';
import { graphFetch } from '../api/graphClient';
import { getVisibleWeekDays } from '../helpers/timesheetHelper';
import { TransactionUnitPriceSource } from './transaction';
import { ITransactionParameters } from '../interface/parameters';

dayjs.extend(utc);

interface ILastWeekDay {
  number: string;
  weekDay: string;
  date: string;
}

export const getLastWeekDays = (sevenDaysAgoDate: string): ILastWeekDay[] => {
  const final: ILastWeekDay[] = [];
  for (let i = 0; i <= 6; i++) {
    const weekDay = dayjs(sevenDaysAgoDate).add(i, 'day');
    final.push({
      number: weekDay.format('D'),
      weekDay: weekDay.format('ddd'),
      date: weekDay.utc(true).toISOString(),
    });
  }
  return final;
};

export interface ISummarizeBillableHours {
  summary: string;
  matterIds: string[];
  date: string;
}
interface IDailyGroupedTransactions {
  date: string;
  amount: number;
  transactions: ITransaction[];
}
export const groupTransactionsByDay = (
  transactionData: ITransaction[],
  last7DaysHourTransactionsInfo?: ILast7DaysHourTransactionDashboard[],
): IDailyGroupedTransactions[] =>
  Object.values(
    transactionData.reduce((acc: Record<string, IDailyGroupedTransactions>, elem: ITransaction) => {
      const elemKey = dayjs(elem.transactionDate).format('YYYY-MM-DD');
      if (!acc[elemKey]) {
        acc[elemKey] = { date: elem.transactionDate, transactions: [], amount: 0 };
      }

      if (!last7DaysHourTransactionsInfo) {
        acc[elemKey].amount += areStringsEqual(elem.unit?.id, SystemConstants.UNIT_HOUR)
          ? (elem.billableQuantity ?? 0)
          : 0;
        acc[elemKey].amount = +acc[elemKey].amount.toFixed(2);
      }
      // Add the transaction to the group
      acc[elemKey].transactions.push(elem);

      return acc;
    }, {}),
  )
    .map((group) => {
      group.transactions.sort((a, b) => {
        return new Date(b?.createdOn).valueOf() - new Date(a.createdOn).valueOf();
      });

      const totalHoursForGroupedDay =
        last7DaysHourTransactionsInfo?.find((x) => x.transactionDate === group.date)
          ?.billableHours ?? 0;

      group.amount = totalHoursForGroupedDay;

      return group;
    })
    .sort((a, b) => {
      return new Date(b.date).valueOf() - new Date(a.date).valueOf();
    });
export const summarizeBillableHours = (
  data: ITransaction[],
  sevenDaysAgoDate: string,
  useCommaseparator: boolean = false,
): ISummarizeBillableHours[] => {
  const grouped = groupTransactionsByDay(data);
  const lastWeek = getLastWeekDays(sevenDaysAgoDate); // assumes date objects or { date: string }

  const sums: ISummarizeBillableHours[] = lastWeek.map((el) => {
    const group = grouped.find(
      (g) => dayjs(g.date).format('YYYY-MM-DD') === dayjs(el.date).format('YYYY-MM-DD'),
    );

    const amount =
      Math.floor(group?.amount ?? 0) === group?.amount
        ? group?.amount.toFixed(0)
        : group?.amount.toFixed(2);

    const formattedHoursWithSeparator = useCommaseparator ? amount?.replace('.', ',') : amount;
    return {
      summary: formattedHoursWithSeparator ?? '0',
      matterIds: group?.transactions.map((t) => t.matterId) ?? [],
      date: dayjs(el.date).format('YYYY-MM-DD'),
    };
  });

  return sums;
};

interface IRowData {
  hours: string | null;
  date: string;
  totalTransactions: number | null;
}

export interface IWeeklyDataRow {
  matter: IMatter;
  principalCompany?: IPrincipalCompany;
  principalPrivatePerson?: IPrincipalPrivatePerson;
  rowData: IRowData[];
}

export const weeklyDataRow = (
  data: ITransaction[],
  nineDaysAgoDate: string,
  temporaryMatter?: IMatter,
  searchValue?: string,
) => {
  const final: IWeeklyDataRow[] = [];
  const unique = [...new Map(data.map((el) => [el.matterId, el])).values()];
  const visibleDays = getVisibleWeekDays(nineDaysAgoDate, data);

  unique.forEach((el) => {
    const all = data.filter((e) => e.matterId === el.matterId);
    const rowData: IRowData[] = [];

    visibleDays.forEach((day) => {
      const dayMoment = dayjs(day.date);

      let transactionExistsOnCurrentDate = false;

      const hours = all
        .filter(
          (e) => dayMoment.format('YYYY-MM-DD') === dayjs(e.transactionDate).format('YYYY-MM-DD'),
        )
        ?.reduce((a, b) => a + b.billableQuantity, 0);

      transactionExistsOnCurrentDate =
        all.some(
          (e) => dayMoment.format('YYYY-MM-DD') === dayjs(e.transactionDate).format('YYYY-MM-DD'),
        ) ?? false;

      const transactions = all.filter(
        (e) => dayMoment.format('YYYY-MM-DD') === dayjs(e.transactionDate).format('YYYY-MM-DD'),
      );

      const formattedHours = Math.floor(hours) === hours ? hours.toFixed(0) : hours.toFixed(2);
      const formattedHoursWithSeparator = usesCommaDecimalSeparator()
        ? formattedHours.replace('.', ',')
        : formattedHours;

      rowData.push(
        transactionExistsOnCurrentDate
          ? {
              hours: formattedHoursWithSeparator,
              date: dayMoment.format('YYYY-MM-DD'),
              totalTransactions: transactions.length,
            }
          : { hours: null, date: dayMoment.format('YYYY-MM-DD'), totalTransactions: null },
      );
    });

    final.push({
      matter: el.matter,
      rowData,
      principalCompany: el.principalCompany,
      principalPrivatePerson: el.principalPrivatePerson,
    });
  });

  if (temporaryMatter) {
    const matchesSearch =
      !searchValue ||
      temporaryMatter.subject?.toLowerCase().includes(searchValue.toLowerCase()) ||
      temporaryMatter.matterNumber?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
      temporaryMatter.principalCompany?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      temporaryMatter.principalPrivatePerson?.fullName
        ?.toLowerCase()
        .includes(searchValue.toLowerCase());

    if (matchesSearch) {
      const rowData: IRowData[] = [];
      visibleDays.forEach((day) => {
        const dayMoment = dayjs(day.date);
        rowData.push({
          hours: null,
          date: dayMoment.format('YYYY-MM-DD'),
          totalTransactions: null,
        });
      });

      final.push({
        matter: temporaryMatter,
        rowData,
        principalCompany: temporaryMatter?.principalCompany,
        principalPrivatePerson: temporaryMatter?.principalPrivatePerson,
      });
    }
  }
  return final;
};
export const checkAttachmentSizeCompliance = async (itemId: string): Promise<boolean> => {
  const restId = Office.context.mailbox.convertToRestId(
    itemId,
    Office.MailboxEnums.RestVersion.v2_0,
  );

  const response = await graphFetch(`${graphApiBaseUrl}/messages/${restId}?$expand=attachments`);

  if (!response.ok) {
    console.error(`Failed to fetch message: ${response.statusText}`);
    throw new Error(`Failed to fetch message: ${response.status}`);
  }

  const message = await response.json();
  const attachments = message.attachments || [];
  // Check if all non-inline attachments are <= 1MB
  return attachments.every((att: { isInline: boolean; size: number }) => {
    if (att.isInline) return true;
    return att.size <= MAX_FILE_SIZE;
  });
};

export const TransactionUnitPriceSourceTranslationMapping = {
  [TransactionUnitPriceSource.NOT_SET]: 'transaction:transactionUnitPriceSource.notSet',
  [TransactionUnitPriceSource.MANUAL]: 'transaction:transactionUnitPriceSource.manual',
  [TransactionUnitPriceSource.MATTER]: 'transaction:transactionUnitPriceSource.matter',
  [TransactionUnitPriceSource.MATTER_ROLE]: 'transaction:transactionUnitPriceSource.matterRole',
  [TransactionUnitPriceSource.CUSTOMER]: 'transaction:transactionUnitPriceSource.customer',
  [TransactionUnitPriceSource.CUSTOMER_CONTACT_ROLE]:
    'transaction:transactionUnitPriceSource.customerContactRole',
  [TransactionUnitPriceSource.CUSTOMER_ROLE]: 'transaction:transactionUnitPriceSource.customerRole',
  [TransactionUnitPriceSource.USER]: 'transaction:transactionUnitPriceSource.user',
  [TransactionUnitPriceSource.USER_ROLE]: 'transaction:transactionUnitPriceSource.userRole',
  [TransactionUnitPriceSource.TRANSACTION_TYPE]:
    'transaction:transactionUnitPriceSource.transactionType',
  [TransactionUnitPriceSource.MATTER_TRANSACTION_TYPE]:
    'transaction:transactionUnitPriceSource.matterTransactionType',
  [TransactionUnitPriceSource.TEAM_MEMBER]: 'transaction:transactionUnitPriceSource.teamMember',
  [TransactionUnitPriceSource.INTERNAL_MATTER]:
    'transaction:transactionUnitPriceSource.internalMatter',
  [TransactionUnitPriceSource.SUBSCRIPTION]: 'transaction:transactionUnitPriceSource.subscription',
  [TransactionUnitPriceSource.MATTER_SUB_CONTRACT]:
    'transaction:transactionUnitPriceSource.matterSubContract',
  [TransactionUnitPriceSource.PRINCIPAL]: 'transaction:transactionUnitPriceSource.principal',
  [TransactionUnitPriceSource.PRINCIPAL_PARENT_CUSTOMER]:
    'transaction:transactionUnitPriceSource.principalParentCustomer',
  [TransactionUnitPriceSource.MATTER_TEAM_MEMBER]:
    'transaction:transactionUnitPriceSource.matterTeamMember',
  [TransactionUnitPriceSource.PRINCIPAL_TEAM_MEMBER]:
    'transaction:transactionUnitPriceSource.principalTeamMember',
  [TransactionUnitPriceSource.PRINCIPAL_PARENT_CUSTOMER_TEAM_MEMBER]:
    'transaction:transactionUnitPriceSource.principalParentCustomerTeamMember',
  [TransactionUnitPriceSource.PRINCIPAL_ROLE]:
    'transaction:transactionUnitPriceSource.principalRole',
  [TransactionUnitPriceSource.PRINCIPAL_PARENT_CUSTOMER_ROLE]:
    'transaction:transactionUnitPriceSource.principalParentCustomerRole',
  [TransactionUnitPriceSource.USER_PRICING_LIST]:
    'transaction:transactionUnitPriceSource.userPricingList',
};
export const handleHourPriceRounding = (
  billableAmount: string,
  isTransactionTypeHourBased: boolean,
  transaction: ITransaction | null,
  matterTransactionParameterDetail: ITransactionParameters,
  parameters: ITransactionParameters | undefined,
): { roundedAmount: string; roundingApplied: boolean } => {
  if (!isTransactionTypeHourBased) return { roundedAmount: billableAmount, roundingApplied: false };
  if (transaction?.transactionType?.standardPrice)
    return { roundedAmount: billableAmount, roundingApplied: false };
  if (billableAmount === '9999.99')
    return { roundedAmount: billableAmount, roundingApplied: false };
  const invoiceableDurationStep =
    matterTransactionParameterDetail?.invoiceableDurationStep ??
    parameters?.invoiceableDurationStep ??
    InvoiceableDurationStep.NONE;
  if (
    Number(billableAmount) <= matterTransactionParameterDetail?.minimumInvoiceableDurationInHours &&
    matterTransactionParameterDetail?.minimumInvoiceableDurationInHours !== 0
  ) {
    return {
      roundingApplied: true,
      roundedAmount:
        applyRoundingRuleToBillableHours(
          matterTransactionParameterDetail?.minimumInvoiceableDurationInHours,
          invoiceableDurationStep,
        ).toString() ?? 0,
    };
  } else if (
    invoiceableDurationStep !== InvoiceableDurationStep.NONE &&
    Number(billableAmount) !== 0
  ) {
    return {
      roundingApplied: true,
      roundedAmount:
        applyRoundingRuleToBillableHours(
          Number(billableAmount),
          invoiceableDurationStep,
        ).toString() ?? 0,
    };
  } else {
    return { roundedAmount: String(billableAmount), roundingApplied: false };
  }
};
